import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CompanyUsersFilter, Pageable, PermissionTypesEnum, UserCompanyUserPreviewOutDTO } from '@api/ringil-api.v2.tsx';
import SvgId from '@icons/id.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, OptionType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { Chip } from 'primereact/chip';
import { EditUserSidebar } from '@app/pages/customer/CompanyProfile/sidebar/EditUserSidebar.tsx';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import SvgDeactivate from '@icons/delete-trash.svg?react';
import SvgGenerateToken from '@icons/share-nodes-inverted.svg?react';
import MoreButton, { MoreMenuSection } from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import GenerateNewIntegrationToken from '@components/obsolete/CompanyProfile/dialogs/GenerateNewIntegrationToken.tsx';
import { DeactivateUserDialog } from '@components/obsolete/CompanyProfile/DeactivateUserDialog.tsx';
import { AddWebAccessSidebar } from '@components/obsolete/CompanyProfile/AddWebAccessSidebar.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { isNotNullOrUndefined } from '@utils/utils';

interface ComponentProps {
  companyId: string;
}

/** Tri-state the registration dropdown stores; resolved to the `isRegistered` boolean (or omitted) in normalizeFilter. */
enum RegistrationFilterStateEnum {
  ALL = 'all',
  REGISTERED = 'registered',
  NOT_REGISTERED = 'notRegistered',
}

/** Tri-state the account status dropdown stores; resolved to the `isActive` boolean (or omitted) in normalizeFilter. */
enum AccountStatusFilterStateEnum {
  ALL = 'all',
  ACTIVE = 'active',
  NOT_ACTIVE = 'notActive',
}

interface CompanyUsersListFilter extends CompanyUsersFilter {
  /** UI-only field driving the registration dropdown. Resolved to `isRegistered` at fetch time. */
  registrationState?: RegistrationFilterStateEnum;
  /** UI-only field driving the account status dropdown. Resolved to `isActive` at fetch time. */
  accountState?: AccountStatusFilterStateEnum;
}

interface CreateEditUser {
  visible: boolean;
  userId?: string;
  phoneNumber?: string;
}

const CompanyProfileUsers: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();

  const addUserFlag = useSecuredFlag('add-company-user', {
    kind: 'action',
    description: 'Add new user to company',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_CREATE_USER],
  });

  const {
    listCompanyUsers,
    lovSecurityRolesV2,
  } = useContext(ApiContext);

  const [changeStatusUserId, setChangeStatusUserId] = useState<string>();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [addWebAccessForm, setAddWebAccessForm] = useState<CreateEditUser>({
    visible: false,
  });


  const [createEditUserForm, setCreateEditUserForm] = useState<CreateEditUser>({
    visible: false,
  });

  const tableRef = useRef<{ reloadData: () => void, selectedObjects: UserCompanyUserPreviewOutDTO[], clear: () => void }>(null);


  // MoreButton drops items whose `visible` is false itself, so the flags stay declarative here.
  const generateNavLink = (rowData: UserCompanyUserPreviewOutDTO): MoreMenuSection[] => [
    {
      items: [
        {
          id: 'changeStatus',
          label: rowData.isActive ? tr(`CompanyProfile.deactivate`, `Deactivate`) : tr(`CompanyProfile.activate`, `Activate`),
          icon: <SvgDeactivate />,
          command: () => {
            setChangeStatusUserId(rowData.userId);
          },
        },
        {
          id: 'addWebAccess',
          label: tr(`CompanyProfile.user_link`, `Vytvorit pristup`),
          command: () => {
            setAddWebAccessForm({ visible: true, userId: rowData.userId });
          },
          visible: rowData.isVirtual,
        },
        {
          id: 'generateToken',
          label: tr(`CompanyProfile.generateToken`, `Generate API token`),
          icon: <SvgGenerateToken />,
          command: () => {
            setSelectedUserId(rowData.userId);
          },
        },
      ],
    },
  ];

  const [roleOptions, setRoleOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    lovSecurityRolesV2(roles =>
      setRoleOptions(
        roles
          .filter(r => !r.isOnlyInternal && !r.isPrivate)
          .map(r => ({ label: r.translateKey ? tr(r.translateKey) : r.name, value: r.id, active: true })),
      ),
    );
  }, []);

  const loadDataApi = (filter: CompanyUsersFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<UserCompanyUserPreviewOutDTO>) => void) => {
    listCompanyUsers(
      pageable,
      filter,
      data => {
        onSuccess(data);
      });
  };

  const normalizeFilter = (value?: CompanyUsersListFilter): CompanyUsersListFilter => {
    const result: CompanyUsersListFilter = { ...(value ?? {}) };

    // UI-only tri-states -> the booleans the API expects. ALL omits the key entirely so the backend
    // returns both variants. Fall back to the booleans when only those are present (e.g. from a saved filter).
    const registrationState =
      result.registrationState ??
      (isNotNullOrUndefined(result.isRegistered)
        ? result.isRegistered
          ? RegistrationFilterStateEnum.REGISTERED
          : RegistrationFilterStateEnum.NOT_REGISTERED
        : RegistrationFilterStateEnum.ALL);
    delete result.registrationState;
    if (registrationState === RegistrationFilterStateEnum.ALL) {
      delete result.isRegistered;
    } else {
      result.isRegistered = registrationState === RegistrationFilterStateEnum.REGISTERED;
    }

    const accountState =
      result.accountState ??
      (isNotNullOrUndefined(result.isActive)
        ? result.isActive
          ? AccountStatusFilterStateEnum.ACTIVE
          : AccountStatusFilterStateEnum.NOT_ACTIVE
        : AccountStatusFilterStateEnum.ALL);
    delete result.accountState;
    if (accountState === AccountStatusFilterStateEnum.ALL) {
      delete result.isActive;
    } else {
      result.isActive = accountState === AccountStatusFilterStateEnum.ACTIVE;
    }

    return result;
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CompanyProfile.searchFirstName`, `First name`),
      value: ['firstName'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CompanyProfile.searchLastName`, `Last name`),
      value: ['lastName'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CompanyProfile.searchEmail`, `Email`),
      value: ['email'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`CompanyProfile.filterRole`, `Role`),
      value: ['securityRoleIds'],
      options: roleOptions,
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`CompanyProfile.filterRegistrationStatus`, `Registration status`),
      label: tr(`CompanyProfile.filterRegistrationStatus`, `Registration status`),
      value: ['registrationState'],
      options: [
        { label: tr(`CompanyProfile.filterRegistrationAll`, `All`), value: RegistrationFilterStateEnum.ALL, active: true },
        { label: tr(`CompanyProfile.filterRegistered`, `Registered`), value: RegistrationFilterStateEnum.REGISTERED, active: true },
        { label: tr(`CompanyProfile.filterNotRegistered`, `Not registered`), value: RegistrationFilterStateEnum.NOT_REGISTERED, active: true },
      ],
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`CompanyProfile.filterAccountStatus`, `Account status`),
      label: tr(`CompanyProfile.filterAccountStatus`, `Account status`),
      value: ['accountState'],
      options: [
        { label: tr(`CompanyProfile.filterAccountAll`, `All`), value: AccountStatusFilterStateEnum.ALL, active: true },
        { label: tr(`CompanyProfile.filterAccountActive`, `Active`), value: AccountStatusFilterStateEnum.ACTIVE, active: true },
        { label: tr(`CompanyProfile.filterAccountNotActive`, `Non active`), value: AccountStatusFilterStateEnum.NOT_ACTIVE, active: true },
      ],
    },
  ];

  const renderUserNameColumn = (rowData: UserCompanyUserPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {rowData.fullName}
        </span>
        <span>
          {rowData.phone}
        </span>
        <span className="break-all max-w-full">
          {rowData.email}
        </span>
      </div>
    );
  };

  const renderRolesColumn = (rowData: UserCompanyUserPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <div className={'flex flex-wrap gap-1'}>
          {rowData.roles.map((item, index) => (
            <div
              key={index}
              className={'flex justify-center items-center border border-solid border-content-primary rounded px-2 py-1'}
            >
              {tr(`CompanyProfile.role${item}`, item)}
            </div>
          ))}
        </div>
        <div className={'flex flex-wrap gap-1'}>
          {rowData.securityRoles.filter(item => !item.isOnlyInternal).map((item, index) => (
            <div
              key={index}
              className={'flex justify-center items-center border border-solid border-content-primary rounded px-2 py-1'}
            >
              {item.name}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStatusColumn = (rowData: UserCompanyUserPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start justify-start gap-2'}>
        {rowData.isRegistered &&
          <span className="flex justify-center items-center px-2 py-1 rounded bg-complementary-successMedium">
            {tr(`CompanyProfile.registered`, `Registered`)}
          </span>
        }
        {rowData.isVirtual &&
          <span className="flex justify-center items-center px-2 py-1 rounded bg-complementary-errorMedium">
            {tr(`CompanyProfile.noAccessOnlyDriver`, `No access`)}
          </span>
        }
        {rowData.isActive &&
          <span className="flex justify-center items-center px-2 py-1 rounded bg-complementary-successMedium">
            {tr(`CompanyProfile.userIsActive`, `Active`)}
          </span>
        }
        {!rowData.isActive &&
          <span className="flex justify-center items-center px-2 py-1 rounded bg-content-brandMedium">
            {tr(`CompanyProfile.userNotActive`, `Not active`)}
          </span>
        }
        {rowData.hasToken ?
          <Chip template="API" /> : <></>
        }
      </div>
    );
  };

  const renderActionsColumn = (rowData: UserCompanyUserPreviewOutDTO) => {
    return (
      <div className={'flex flex-row items-center justify-start gap-2'}>
        <RingilButton
          mode={'table'}
          icon={<SvgEdit />}
          onClick={() => setCreateEditUserForm({ visible: true, userId: rowData.userId })}
        />
        <MoreButton buttonMode={'table'} sections={generateNavLink(rowData)} />
      </div>
    );
  };

  const columnsConfig: ColumnConfig<UserCompanyUserPreviewOutDTO>[] = [
    {
      id: 'name',
      header: 'CompanyProfile.columnHeaderUsersName',
      headerDefault: 'Name',
      width: '37%',
      sortable: false,
      field: 'name',
      order: 1,
      render: renderUserNameColumn,
    },
    {
      id: 'roles',
      header: 'CompanyProfile.columnHeaderUsersRoles',
      headerDefault: 'Roles',
      width: '37%',
      sortable: false,
      field: 'roles',
      order: 2,
      render: renderRolesColumn,
    },
    {
      id: 'status',
      header: 'CompanyProfile.columnHeaderUsersStatus',
      headerDefault: 'Status',
      width: '16%',
      sortable: false,
      order: 3,
      render: renderStatusColumn,
    },
    {
      id: 'actions',
      header: 'CompanyProfile.columnHeaderUsersActions',
      headerDefault: 'Actions',
      width: '10%',
      sortable: false,
      order: 4,
      render: renderActionsColumn,
    },
  ];


  return (
    <>
      <div className={'flex flex-row mb-2'}>
        <COHeadline
          variant={'h2'}
          title={tr('CompanyProfile.allUsers', 'All users')}
          icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
        />
        <SecuredArea id="company-profile-add-user" label="Add new user button" description="Button that adds a new user to the company" visible={addUserFlag}>
          <RingilButton
            icon={<SvgPlus />}
            label={tr(`CompanyProfile.addNewUser`, `Přidat uživatele`)}
            mode={'link'}
            onClick={() => setCreateEditUserForm({ visible: true })}
          />
        </SecuredArea>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'company_profile_users_list'}
        normalizeFilter={normalizeFilter}
      />

      <EditUserSidebar
        visible={createEditUserForm.visible}
        onHide={() => {
          setCreateEditUserForm({ visible: false });
        }}
        onComplete={() => {
          tableRef.current?.reloadData();
          setCreateEditUserForm({ visible: false });
        }}
        userId={createEditUserForm.userId}
        companyId={companyId}
      />

      <DeactivateUserDialog
        userId={changeStatusUserId}
        onHide={() => setChangeStatusUserId(null)}
        onComplete={() => {
          setChangeStatusUserId(null);
          tableRef.current?.reloadData();
        }}
      />

      <AddWebAccessSidebar
        visible={addWebAccessForm.visible}
        onHide={() => {
          setAddWebAccessForm({ visible: false });
        }}
        onComplete={() => {
          tableRef.current?.reloadData();
          setAddWebAccessForm({ visible: false });
        }}
        userId={addWebAccessForm.userId}
      />

      <GenerateNewIntegrationToken
        onHide={() => setSelectedUserId(null)}
        selectedUserId={selectedUserId}
      />
    </>
  );
};


export default CompanyProfileUsers;
